import React from 'react';
import { useSurvey } from '@contexts/SurveyContext';
import { 
  User, Truck, Calendar, FileText, Info, MapPin, 
  Ruler, Weight, Clock, AlertTriangle, Tag, Clipboard
} from 'lucide-react';

export const SurveyInfoTab = () => {
  const { activeSurvey } = useSurvey();

  if (!activeSurvey) {
    return (
      <div className="p-4 text-center text-gray-400 h-full flex items-center justify-center">
        <div>
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>No active survey selected</p>
        </div>
      </div>
    );
  }

  // Format date strings
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format dimensions
  const formatDimensions = (vehicle) => {
    if (!vehicle) return 'N/A';
    const { length, width, height } = vehicle;
    if (!length && !width && !height) return 'N/A';
    return `${length || '?'}' × ${width || '?'}' × ${height || '?'}'`;
  };

  // Format weight
  const formatWeight = (vehicle) => {
    if (!vehicle?.weight) return 'N/A';
    const unit = vehicle.weightUnit || 'lbs';
    return `${vehicle.weight} ${unit}`;
  };

  // Format high pole heights
  const formatHighPole = (highPoleHeights) => {
    if (!highPoleHeights) return 'Not Required';
    if (!highPoleHeights.required) return 'Not Required';
    return `${highPoleHeights.value} ${highPoleHeights.unit || 'ft'}`;
  };

  // Group definitions with their icons and fields
  const groups = [
    {
      id: 'basic',
      label: 'Basic Information',
      icon: Info,
      fields: [
        { label: 'Name', value: activeSurvey.name },
        { label: 'Customer', value: activeSurvey.customerName || 'N/A' },
        { label: 'PO Number', value: activeSurvey.customerPO || 'N/A' },
        { label: 'Survey Type', value: activeSurvey.surveyType || 'Standard' }
      ]
    },
    {
      id: 'vehicle',
      label: 'Vehicle Details',
      icon: Truck,
      fields: [
        { label: 'Type', value: activeSurvey.vehicle?.type || 'N/A' },
        { label: 'Dimensions', value: formatDimensions(activeSurvey.vehicle) },
        { label: 'Weight', value: formatWeight(activeSurvey.vehicle) }
      ]
    },
    {
      id: 'route',
      label: 'Route Information',
      icon: MapPin,
      fields: [
        { label: 'Origin', value: activeSurvey.origin || 'N/A' },
        { label: 'Destination', value: activeSurvey.destination || 'N/A' },
        { label: 'High Pole', value: formatHighPole(activeSurvey.highPoleHeights) }
      ]
    },
    {
      id: 'restrictions',
      label: 'Restrictions & Requirements',
      icon: AlertTriangle,
      fields: [
        { label: 'Pilot Car', value: activeSurvey.pilotCarRequired ? 'Required' : 'Not Required' },
        { label: 'Permits', value: activeSurvey.permitsRequired ? 'Required' : 'Not Required' },
        { label: 'Restrictions', value: activeSurvey.restrictions || 'None' }
      ]
    },
    {
      id: 'notes',
      label: 'Notes',
      icon: Clipboard,
      fields: [
        { label: 'Notes', value: activeSurvey.notes || 'No notes' }
      ]
    },
    {
      id: 'metadata',
      label: 'Metadata',
      icon: Tag,
      fields: [
        { label: 'Created', value: formatDate(activeSurvey.createdAt) },
        { label: 'Last Updated', value: formatDate(activeSurvey.updatedAt) },
        { label: 'Status', value: activeSurvey.status || 'Draft' }
      ]
    }
  ];

  // Add any additional fields that aren't already included
  const includedFields = new Set([
    'name', 'customerName', 'customerPO', 'surveyType', 'vehicle', 
    'origin', 'destination', 'highPoleHeights', 'pilotCarRequired', 
    'permitsRequired', 'restrictions', 'notes', 'createdAt', 'updatedAt', 
    'status', 'id', 'ownerId', 'sharedWith'
  ]);

  // Check for any additional fields in the survey object
  const additionalFields = [];
  Object.entries(activeSurvey).forEach(([key, value]) => {
    if (!includedFields.has(key) && 
        key !== 'id' && 
        key !== 'ownerId' && 
        key !== 'sharedWith' &&
        typeof value !== 'object') {
      additionalFields.push({ label: key, value: value || 'N/A' });
    }
  });

  // Add additional fields group if needed
  if (additionalFields.length > 0) {
    groups.push({
      id: 'additional',
      label: 'Additional Information',
      icon: FileText,
      fields: additionalFields
    });
  }

  return (
    <div className="p-4 space-y-6 overflow-y-auto max-h-[calc(100vh-200px)]">
      {groups.map(group => (
        <div 
          key={group.id} 
          className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden"
        >
          <div className="flex items-center gap-3 p-3 bg-gray-700/50 border-b border-gray-700">
            <group.icon className="w-5 h-5 text-blue-400" />
            <h3 className="text-sm font-medium text-white">{group.label}</h3>
          </div>
          
          <div className="p-3 space-y-3">
            {group.fields.map((field, index) => (
              <div key={index} className={field.value === 'N/A' ? 'opacity-60' : ''}>
                <label className="block text-xs font-medium text-gray-400 mb-1">{field.label}</label>
                <div className="text-sm text-white break-words">
                  {typeof field.value === 'string' && field.value.length > 100 
                    ? (
                      <div className="bg-gray-700/50 p-2 rounded-md max-h-24 overflow-y-auto">
                        {field.value}
                      </div>
                    ) 
                    : field.value
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SurveyInfoTab;